import { notification } from 'antd';
import posthog from 'posthog-js';
import linkedinOutreachApi from './linkedinOutreachApi';
import { cadencesApi } from '../../pipeline/services/cadencesApi';
import { getQueryClient } from '../../../shared/services/queryClientRef';
import { lerDiagnosticoDoResultadoLinkedIn } from '../../integrations/lib/linkedinSendStatus';
import {
  pertenceAoEscopoDoEnriquecimento,
  selecionarEntradasDoEscopoAtual,
  useEnrichmentQueueStore,
  type EnrichmentQueueEntry,
} from '../../../shared/stores/useEnrichmentQueueStore';

/**
 * Motor de acompanhamento dos envios LinkedIn fire-and-forget (spec UX + ADR
 * 2026-07-23).
 *
 * O clique só espera o ENQUEUE (202); o desfecho real (10s–minutos, fila
 * Playwright do scraper) é acompanhado aqui, fora do ciclo de vida do modal:
 *
 * - a entrada vive na fila global do header (`useEnrichmentQueueStore`, tipo
 *   `linkedin_send`, persistida em localStorage) — o chip "Enviando…" do passo
 *   e a linha do popover leem a MESMA fonte, e um F5 não perde o
 *   acompanhamento (`resumeLinkedinJobs()` retoma o polling no boot);
 * - sucesso/já-convidado/já-conectado → notification (8s) e conclusão do
 *   passo via PATCH (fallback do soak — o SERVIDOR também conclui via
 *   writeback; o PATCH é idempotente e vira no-op);
 * - falha → notification persistente com o motivo real; o chip do card vira
 *   "Falhou — re-tentar" (draft preservado na entrada).
 *
 * O registro DURÁVEL de falha (sobrevive a navegador fechado) é o sino,
 * emitido pelo backend — este motor é a camada de UX imediata.
 */

interface TrackArgs {
  jobId: string;
  kind: 'conexao' | 'mensagem';
  contactName?: string;
  company?: string;
  dealId?: string;
  cadenceId?: string;
  stepId?: string;
  /** Texto enviado — preservado na entrada p/ o re-tentar reabrir com draft. */
  draft?: string;
}

const active = new Set<string>();

const POLL_MS = 5_000;
const MAX_MS = 10 * 60_000;

const store = () => useEnrichmentQueueStore.getState();

function label(kind: 'conexao' | 'mensagem', contactName?: string, company?: string): string {
  const alvo = [contactName, company].filter(Boolean).join(' — ');
  return `${kind === 'conexao' ? 'Convite' : 'Mensagem'} · ${alvo || 'LinkedIn'}`;
}

/** Registra a entrada na fila global e inicia o acompanhamento. */
export function trackLinkedinJob(args: TrackArgs): void {
  const { jobId, kind, contactName, company, dealId, cadenceId, stepId, draft } = args;
  store().enqueue({
    messageId: jobId,
    type: 'linkedin_send',
    label: label(kind, contactName, company),
    dealId: dealId || '',
    contactCoreId: '',
    startedAt: Date.now(),
    cadenceId,
    stepId,
    sendKind: kind,
    draft,
  });
  followLinkedinJob(jobId);
}

/** Retoma o polling dos envios em voo persistidos (chamar no boot do shell —
 *  EnrichmentQueueIcon monta em todas as telas autenticadas). Entradas do rail
 *  Unipile (id `unipile:…`) não têm job no scraper para re-poll — um F5 no
 *  meio dos ~2s de resolução as marca `lost` (informativo). */
export function resumeLinkedinJobs(): void {
  for (const e of selecionarEntradasDoEscopoAtual(store().entries)) {
    if (e.type !== 'linkedin_send' || e.status !== 'processing') continue;
    if (e.messageId.startsWith('unipile:')) {
      store().markStatus(e.messageId, 'failed', { failKind: 'lost' });
      continue;
    }
    followLinkedinJob(e.messageId);
  }
}

/** Rail SÍNCRONO (Unipile, ~1-3s) unificado no contrato pós-clique: registra
 *  a entrada na fila e devolve o resolvedor — o chamador fecha o modal na
 *  hora e resolve quando a promise do envio retornar. Mesmo modelo mental do
 *  rail Live ("cliquei, está em envio, serei avisado"). */
export function registerImmediateLinkedinSend(args: Omit<TrackArgs, 'jobId'>): {
  entryId: string;
  resolve: (outcome: string) => Promise<void>;
} {
  const entryId = `unipile:${args.stepId ?? 'avulso'}:${Date.now()}`;
  store().enqueue({
    messageId: entryId,
    type: 'linkedin_send',
    label: label(args.kind, args.contactName, args.company),
    dealId: args.dealId || '',
    contactCoreId: '',
    startedAt: Date.now(),
    cadenceId: args.cadenceId,
    stepId: args.stepId,
    sendKind: args.kind,
    draft: args.draft,
  });
  return {
    entryId,
    resolve: async (outcome: string) => {
      const entry = entryFor(entryId);
      if (entry) await resolveOutcome(entry, outcome);
    },
  };
}

function entryFor(jobId: string): EnrichmentQueueEntry | undefined {
  return selecionarEntradasDoEscopoAtual(store().entries).find((e) => e.messageId === jobId);
}

/** Cancela um envio ainda em voo (rail Live). Retorna:
 *  - 'cancelled': cancelado em definitivo (job ainda não tinha começado) →
 *    a entrada some;
 *  - 'requested': pedido registrado num job já em execução (best-effort — o
 *    worker aborta ANTES do envio se ainda der tempo) → a entrada continua no
 *    polling e resolve como cancelled OU sent;
 *  - 'too_late': o envio já saiu/finalizou.
 *  O rail Unipile (`unipile:…`) resolve em ~1-3s e não tem job cancelável. */
export async function cancelLinkedinSend(
  jobId: string,
): Promise<'cancelled' | 'requested' | 'too_late'> {
  if (jobId.startsWith('unipile:')) return 'too_late';
  if (!entryFor(jobId)) return 'too_late';
  try {
    const r = await linkedinOutreachApi.cancelJob(jobId);
    if (r.cancelled) {
      active.delete(jobId);
      store().removeOnClick(jobId);
      return 'cancelled';
    }
    return r.cancel_requested ? 'requested' : 'too_late';
  } catch {
    return 'too_late';
  }
}

function followLinkedinJob(jobId: string): void {
  if (active.has(jobId)) return;
  active.add(jobId);
  const finish = () => active.delete(jobId);
  const startedAt = entryFor(jobId)?.startedAt ?? Date.now();

  const tick = async (): Promise<void> => {
    const entry = entryFor(jobId);
    if (!entry) {
      // Entrada removida (GC/limpeza da fila) — nada mais a acompanhar.
      finish();
      return;
    }
    if (!pertenceAoEscopoDoEnriquecimento(entry)) {
      finish();
      return;
    }
    try {
      const s = await linkedinOutreachApi.getJobStatus(jobId);
      if (!pertenceAoEscopoDoEnriquecimento(entry)) {
        finish();
        return;
      }
      if (s.status !== 'done' && s.status !== 'error') {
        if (Date.now() - startedAt > MAX_MS) {
          await resolveOutcome(entry, 'lost');
          finish();
          return;
        }
        window.setTimeout(() => void tick(), POLL_MS);
        return;
      }
      const diagnostic = lerDiagnosticoDoResultadoLinkedIn(s.result);
      const outcome = diagnostic.outcome ?? (s.status === 'error' ? 'error' : undefined);
      await resolveOutcome(entry, outcome, {
        step: diagnostic.step,
        http: diagnostic.http,
      });
      finish();
    } catch {
      if (Date.now() - startedAt > MAX_MS) {
        store().markStatus(jobId, 'failed', { failKind: 'lost' });
        finish();
        return;
      }
      window.setTimeout(() => void tick(), POLL_MS);
    }
  };

  window.setTimeout(() => void tick(), POLL_MS);
}

async function resolveOutcome(
  entry: EnrichmentQueueEntry,
  outcome: string | undefined,
  diagnostic?: { step?: string; http?: number },
): Promise<void> {
  if (!pertenceAoEscopoDoEnriquecimento(entry)) return;
  try {
    posthog.capture('linkedin_send_outcome', {
      outcome: outcome ?? 'error',
      kind: entry.sendKind,
      elapsed_ms: Date.now() - entry.startedAt,
      rail: entry.messageId.startsWith('unipile:') ? 'unipile' : 'live',
    });
  } catch {
    /* telemetria nunca quebra o desfecho */
  }
  // Cancelado (o usuário abortou dentro da janela) — silencioso: a entrada
  // some, sem notification e sem concluir o passo (nada foi enviado).
  if (outcome === 'cancelled') {
    store().removeOnClick(entry.messageId);
    return;
  }
  const ok = outcome === 'sent' || outcome === 'already_invited' || outcome === 'already_connected';
  if (ok) {
    store().markStatus(entry.messageId, 'completed', { outcome });
    notifyOutcome(entry, outcome as string);
    // Fallback do soak: o servidor conclui via writeback; este PATCH é
    // idempotente (no-op se já done). Falha silenciosa — self-healing.
    if (entry.cadenceId && entry.stepId) {
      try {
        await cadencesApi.updateStep(entry.cadenceId, entry.stepId, { status: 'done' });
      } catch {
        /* servidor conclui; ou o passo se auto-cura no próximo clique */
      }
    }
    // O passo virou "Concluído" — reflete no board NA HORA, sem esperar o
    // próximo refetch (spec UX: transição sem refresh manual).
    if (entry.dealId) {
      getQueryClient()?.invalidateQueries({ queryKey: ['cadences', 'by-deal', entry.dealId] });
    }
    return;
  }
  const failKind: EnrichmentQueueEntry['failKind'] =
    outcome === 'not_connected'
      ? 'not_connected'
      : outcome === 'auth_challenge'
        ? 'auth_challenge'
        : outcome === 'lost'
          ? 'lost'
          : outcome === 'rate_limited'
            ? 'rate_limited'
            : 'failed';
  store().markStatus(entry.messageId, 'failed', {
    failKind,
    outcome,
    failureStep: diagnostic?.step,
    failureHttp: diagnostic?.http,
  });
  notifyOutcome(entry, outcome ?? 'error');
}

function notifyOutcome(entry: EnrichmentQueueEntry, outcome: string): void {
  const nome = entry.label.split('·')[1]?.split('—')[0]?.trim() || 'o contato';
  const conexao = entry.sendKind !== 'mensagem';
  const key = entry.messageId;

  if (outcome === 'sent') {
    notification.success({
      key,
      message: conexao ? `Convite enviado — ${nome} ✓` : `Mensagem enviada — ${nome} ✓`,
      description:
        'Envio confirmado pelo LinkedIn. O passo da cadência foi concluído automaticamente.',
      duration: 8,
    });
    return;
  }
  if (outcome === 'already_invited') {
    notification.success({
      key,
      message: `Convite já existia — ${nome}`,
      description: 'Você já tinha um convite pendente para este contato. O passo foi concluído.',
      duration: 8,
    });
    return;
  }
  if (outcome === 'already_connected') {
    notification.success({
      key,
      message: `Vocês já são conexões — ${nome}`,
      description: 'Não era preciso convidar: vocês já estão conectados. O passo foi concluído.',
      duration: 8,
    });
    return;
  }
  if (outcome === 'not_connected') {
    notification.warning({
      key,
      message: `${nome} ainda não aceitou seu convite`,
      description:
        'O LinkedIn só permite mensagem direta entre conexões. O passo continua ' +
        'pendente — aguarde o aceite ou envie pelo perfil.',
      duration: 0,
    });
    return;
  }
  if (outcome === 'auth_challenge') {
    notification.error({
      key,
      message: 'LinkedIn pediu verificação de segurança',
      description:
        `O envio para ${nome} NÃO foi concluído. Reconecte seu LinkedIn em ` +
        'Integrações e tente novamente.',
      duration: 0,
    });
    return;
  }
  if (outcome === 'lost') {
    notification.warning({
      key,
      message: `Envio sem confirmação — ${nome}`,
      description:
        'Não recebemos a confirmação do LinkedIn em 10 minutos. Se o envio saiu, ' +
        'o passo conclui sozinho na sua próxima tentativa; caso contrário, reabra ' +
        'o passo e tente de novo.',
      duration: 0,
    });
    return;
  }
  if (outcome === 'rate_limited') {
    // Distinto de toda outra falha: aqui não é o ENVIO que falhou, é a CONTA
    // que o LinkedIn sinalizou. Retentar (aqui ou manualmente pelo perfil)
    // piora o bloqueio — nunca oferecer esse caminho, ao contrário do
    // fallback genérico abaixo. Espelha a copy do sino durável
    // (`linkedin_rate_limited` em janus-tenant-management).
    notification.error({
      key,
      message: 'LinkedIn pausou os envios da sua conta',
      description:
        `O envio para ${nome} NÃO foi concluído — o LinkedIn limitou temporariamente ` +
        'esta conta. NÃO tente de novo nem envie manualmente agora: isso piora o ' +
        'bloqueio. Aguarde a liberação — o passo continua pendente.',
      duration: 0,
    });
    return;
  }
  notification.error({
    key,
    message: conexao ? `Convite não enviado — ${nome}` : `Mensagem não enviada — ${nome}`,
    description:
      'Algo falhou no envio pelo LinkedIn. Abra o passo para tentar de novo — ' +
      'sua mensagem foi preservada — ou envie manualmente pelo perfil.',
    duration: 0,
  });
}
